import React, { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import LandingNav from './LandingNav';
import Footer from './Footer';

const DAPPS = [
  {
    name: 'Uniswap',
    category: 'Exchange',
    description: 'A decentralized trading protocol for swapping ERC-20 tokens without order books.'
  },
  {
    name: 'OpenSea',
    category: 'NFT',
    description: 'The world\'s first and largest NFT marketplace for crypto collectibles and non-fungible tokens.'
  },
  {
    name: 'Aave',
    category: 'DeFi',
    description: 'An open-source and non-custodial protocol for earning interest on deposits and borrowing assets.'
  },
  {
    name: 'Axie Infinity',
    category: 'Gaming',
    description: 'A blockchain-based trading and battling game that allows players to earn tokens.'
  },
  {
    name: 'Compound',
    category: 'DeFi',
    description: 'An algorithmic, autonomous interest rate protocol built for developers.'
  },
  {
    name: 'Decentraland',
    category: 'Gaming',
    description: 'A virtual reality platform powered by the Ethereum blockchain where you can create and monetize content.'
  }
];

const FILTERS = ['All DApps', 'DeFi', 'NFT', 'Gaming', 'Exchange'];

const PLANS = [
  { rate: '8% Weekly', detail: 'Entry tier for smaller vault balances. Fund it from the same wallet you use to send and receive.' },
  { rate: '10% Weekly', detail: 'Mid tier for users already staking or swapping inside Trust Vault.' },
  { rate: '12% Weekly', detail: 'Upper published tier. Live terms are confirmed on the plan screen after you connect.' }
];

function PageShell({ children }) {
  return (
    <div className="min-h-screen bg-deepspace text-text-primary">
      <LandingNav />
      <main className="relative z-20 pt-28 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">{children}</div>
      </main>
      <Footer />
    </div>
  );
}

function wrap(page, shell) {
  if (!shell) {
    return <div className="px-4 sm:px-6 lg:px-8 pt-24 pb-16 text-text-primary">{page}</div>;
  }
  return <PageShell>{page}</PageShell>;
}

function PillLink({ to, children, variant = 'solid' }) {
  const className = variant === 'solid'
    ? 'inline-block px-6 py-3 rounded-full bg-gradient-cyber text-white font-semibold'
    : 'inline-block px-6 py-3 rounded-full bg-[#1a1524] border border-white/10 text-white font-semibold';
  return <Link to={to} className={className}>{children}</Link>;
}

function HeroImage({ src, alt }) {
  return (
    <div className="rounded-3xl border border-white/10 bg-[#120d1c]/80 p-2 shadow-[0_0_80px_rgba(168,85,247,0.28)]">
      <img src={src} alt={alt} className="w-full h-auto rounded-2xl object-cover" />
    </div>
  );
}

function FeatureGrid({ cards }) {
  return (
    <div className="grid md:grid-cols-3 gap-5 mt-14">
      {cards.map((card) => (
        <article key={card.title} className="rounded-2xl border border-dark-border bg-dark-card/80 p-6">
          <h2 className="text-xl font-bold mb-3">{card.title}</h2>
          <p className="text-text-secondary">{card.body}</p>
        </article>
      ))}
    </div>
  );
}

function IllustratedPage({
  shell,
  eyebrow,
  title,
  accent,
  lede,
  image,
  alt,
  actions,
  cards,
  notes
}) {
  return wrap(
    <>
      <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
        <div>
          <p className="text-sm uppercase tracking-[0.2em] text-cyber-blue mb-3">{eyebrow}</p>
          <h1 className="text-4xl sm:text-5xl font-bold leading-tight mb-4">
            {title}{' '}
            {accent && (
              <span className="bg-gradient-to-r from-cyber-blue to-cyber-gold bg-clip-text text-transparent">{accent}</span>
            )}
          </h1>
          <p className="text-text-secondary text-lg mb-6">{lede}</p>
          <div className="flex flex-wrap gap-3">{actions}</div>
        </div>
        <HeroImage src={image} alt={alt} />
      </div>
      <FeatureGrid cards={cards} />
      {notes && <p className="text-text-secondary text-sm mt-8 max-w-3xl">{notes}</p>}
    </>,
    shell
  );
}

export function DappsPreview() {
  return (
    <section className="py-16 lg:py-24">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-8 items-center mb-10">
          <div>
            <p className="text-sm uppercase tracking-[0.2em] text-cyber-blue mb-3">DApps</p>
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">Discover Decentralized Applications</h2>
            <p className="text-text-secondary">
              The same self-custody wallet that holds your vault also opens Uniswap, OpenSea, Aave, and the rest of the directory. Trust Vault still screens high-risk approvals before a session starts.
            </p>
          </div>
          <HeroImage src="/images/pages/page-dapps.png" alt="Grid of decentralized applications" />
        </div>
        <div className="grid md:grid-cols-3 gap-4">
          {DAPPS.slice(0, 3).map((app) => (
            <article key={app.name} className="rounded-2xl border border-dark-border bg-dark-card/80 p-6">
              <p className="text-xs text-cyber-blue mb-2">{app.category}</p>
              <h3 className="text-xl font-bold mb-2">{app.name}</h3>
              <p className="text-text-secondary text-sm mb-5">{app.description}</p>
              <Link to="/dapps" className="inline-block px-4 py-2 rounded-full bg-gradient-cyber text-sm font-semibold">Open DApp</Link>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

export function InvestPreview() {
  return (
    <section className="py-16 lg:py-24">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-8 items-center">
          <div>
            <h2 className="text-3xl sm:text-5xl font-bold mb-4">
              Grow Your Wealth with <span className="bg-gradient-to-r from-cyber-blue to-cyber-gold bg-clip-text text-transparent">Web3 Investments</span>
            </h2>
            <p className="text-text-secondary mb-6">
              Stocks and crypto plans stay inside the same cold vault. Published tiers run up to 15% weekly or monthly. The rate you fund is the one shown on the plan after you connect.
            </p>
            <div className="flex flex-wrap gap-3 mb-6">
              {PLANS.map((plan) => (
                <span key={plan.rate} className="px-4 py-1.5 rounded-full bg-gradient-cyber text-sm font-semibold">{plan.rate}</span>
              ))}
            </div>
            <Link to="/invest" className="inline-block px-6 py-3 rounded-full bg-gradient-cyber font-semibold">View investment plans</Link>
          </div>
          <HeroImage src="/images/pages/page-invest.png" alt="Three investment charts rising together" />
        </div>
      </div>
    </section>
  );
}

export function DappsPage({ shell = true }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const initial = FILTERS.includes(searchParams.get('filter')) ? searchParams.get('filter') : 'All DApps';
  const [filter, setFilter] = useState(initial);
  const visible = useMemo(
    () => DAPPS.filter((app) => filter === 'All DApps' || app.category === filter),
    [filter]
  );

  const selectFilter = (item) => {
    setFilter(item);
    setSearchParams(item === 'All DApps' ? {} : { filter: item });
  };

  return wrap(
    <>
      <div className="grid lg:grid-cols-2 gap-8 items-center mb-12">
        <div>
          <p className="text-sm uppercase tracking-[0.2em] text-cyber-blue mb-3">DApps</p>
          <h1 className="text-4xl sm:text-5xl font-bold mb-4">Discover Decentralized Applications</h1>
          <p className="text-text-secondary text-lg">
            Browse exchanges, NFT markets, lending protocols, and games. Every session starts from the Trust Vault wallet, and the Web3 firewall classifies sensitive approvals before they leave it.
          </p>
        </div>
        <HeroImage src="/images/pages/page-dapps.png" alt="Directory of decentralized applications" />
      </div>
      <div className="flex flex-wrap gap-3 mb-8">
        {FILTERS.map((item) => (
          <button
            key={item}
            type="button"
            onClick={() => selectFilter(item)}
            className={`px-4 py-2 rounded-full text-sm font-semibold ${filter === item ? 'bg-gradient-cyber text-white' : 'bg-[#1a1524] text-text-secondary border border-white/10'}`}
          >
            {item}
          </button>
        ))}
      </div>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
        {visible.map((app) => (
          <article key={app.name} className="rounded-2xl border border-dark-border bg-[#12101a] p-6">
            <p className="text-xs uppercase tracking-wider text-cyber-blue mb-3">{app.category}</p>
            <h2 className="text-2xl font-bold mb-3">{app.name}</h2>
            <p className="text-text-secondary mb-6">{app.description}</p>
            <Link to="/auth" className="inline-block px-5 py-2.5 rounded-full bg-gradient-cyber font-semibold">Open DApp</Link>
          </article>
        ))}
      </div>
    </>,
    shell
  );
}

export function InvestPage({ shell = true }) {
  return wrap(
    <>
      <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
        <div>
          <p className="text-sm uppercase tracking-[0.2em] text-cyber-blue mb-3">Invest</p>
          <h1 className="text-4xl sm:text-5xl font-bold leading-tight mb-4">
            Grow Your Wealth with <span className="bg-gradient-to-r from-cyber-blue via-[#2dd4bf] to-cyber-gold bg-clip-text text-transparent">Web3 Investments</span>
          </h1>
          <p className="text-text-secondary text-lg mb-6">
            Stocks and crypto plans live in the same vault as your wallet. Published tiers run up to 15% weekly or monthly. You fund the tier you choose after the wallet is connected.
          </p>
          <div className="flex flex-wrap gap-3">
            <PillLink to="/auth/create">Create Account</PillLink>
            <PillLink to="/auth" variant="ghost">Connect Wallet</PillLink>
          </div>
        </div>
        <HeroImage src="/images/pages/page-invest.png" alt="Investment plan charts" />
      </div>
      <h2 className="text-3xl font-bold mt-16 mb-6">Premium Investment Plans</h2>
      <div className="grid md:grid-cols-3 gap-5">
        {PLANS.map((plan) => (
          <article key={plan.rate} className="rounded-2xl border border-dark-border bg-dark-card/80 p-6">
            <span className="inline-block px-4 py-1.5 rounded-full bg-gradient-cyber text-sm font-semibold mb-4">{plan.rate}</span>
            <p className="text-text-secondary">{plan.detail}</p>
          </article>
        ))}
      </div>
      <p className="text-text-secondary text-sm mt-8">These tiers sit next to the plans already available in the app. Returns are not guaranteed.</p>
    </>,
    shell
  );
}

export function WalletPage({ shell = true }) {
  return (
    <IllustratedPage
      shell={shell}
      eyebrow="Wallet"
      title="See every balance."
      accent="Stay in control."
      lede="Your wallet is the command center for Trust Vault. Total portfolio value, live holdings, and quick actions for send, receive, buy, and swap all sit on one screen."
      image="/images/pages/page-wallet.png"
      alt="Trust Vault wallet dashboard"
      actions={<><PillLink to="/auth">Connect Wallet</PillLink><PillLink to="/swap" variant="ghost">Open swap</PillLink></>}
      cards={[
        { title: 'Unified portfolio', body: 'ETH, BTC, and the assets you add appear as one USD view, with each coin still listed on its own row.' },
        { title: 'Send and receive', body: 'Move funds from the same wallet that later funds staking, escrow, and investment plans.' },
        { title: 'Privacy when you need it', body: 'Hide balances on a shared screen, then open any asset again when you are ready to trade.' }
      ]}
      notes="Self-custody stays with you. Trust Vault does not replace the wallet with a bank balance."
    />
  );
}

export function SwapPage({ shell = true }) {
  return (
    <IllustratedPage
      shell={shell}
      eyebrow="Swap"
      title="Swap assets"
      accent="without leaving the vault."
      lede="Exchange tokens you already hold before you stake or invest. The quote, the direction, and the wallet stay on one panel. External pools such as Uniswap remain in the DApp directory."
      image="/images/pages/page-swap.png"
      alt="Two tokens swapping across a neon path"
      actions={<><PillLink to="/auth">Connect Wallet</PillLink><PillLink to="/dapps?filter=Exchange" variant="ghost">Browse exchanges</PillLink></>}
      cards={[
        { title: 'In-vault swap', body: 'Move from one asset to another without opening a second account or exporting a key.' },
        { title: 'Review first', body: 'Confirm the pair and the amount on the swap screen before the wallet signs.' },
        { title: 'Then put it to work', body: 'A completed swap can fund a staking lock or one of the weekly investment tiers.' }
      ]}
    />
  );
}

export function StakingPage({ shell = true }) {
  return (
    <IllustratedPage
      shell={shell}
      eyebrow="Staking"
      title="Stake what you"
      accent="already hold."
      lede="Lock supported assets from the wallet and keep them inside the vault. Staking is separate from the weekly investment tiers, so you can see which balance is locked and which is free."
      image="/images/pages/page-staking.png"
      alt="Coins locked inside a violet staking ring"
      actions={<><PillLink to="/auth">Connect Wallet</PillLink><PillLink to="/invest" variant="ghost">See investment tiers</PillLink></>}
      cards={[
        { title: 'Lock from the wallet', body: 'Choose an asset you already received or swapped. The lock uses that balance, not a new deposit account.' },
        { title: 'Term before you confirm', body: 'Read the staking term on the screen. Nothing moves until you approve it.' },
        { title: 'Still in the vault', body: 'Staked assets stay under the same cold-vault controls as the rest of the portfolio.' }
      ]}
    />
  );
}

export function CopyTradePage({ shell = true }) {
  return (
    <IllustratedPage
      shell={shell}
      eyebrow="Copy Trade"
      title="Follow a lead."
      accent="Size it yourself."
      lede="Pick a trader from the directory already in the app, set the position size from your wallet, and keep the copied trade next to your own activity."
      image="/images/pages/page-copy-trade.png"
      alt="Two trading panels linked by a beam of light"
      actions={<PillLink to="/auth">Connect Wallet</PillLink>}
      cards={[
        { title: 'Choose a lead', body: 'Open the trader list, read the record, and decide whether that style fits the size you want to use.' },
        { title: 'Your size, your wallet', body: 'The copy uses funds from the Trust Vault wallet. You set how much follows the lead.' },
        { title: 'Pause when you want', body: 'Copied positions stay visible with the rest of the vault, so you can stop following without hunting through another app.' }
      ]}
      notes="Copy trading does not remove market risk. A lead's past results are not a promise for the next trade."
    />
  );
}

export function NftsPage({ shell = true }) {
  return (
    <IllustratedPage
      shell={shell}
      eyebrow="NFTs"
      title="Collectibles from"
      accent="the same wallet."
      lede="Marketplaces and game worlds open through the DApp directory. OpenSea covers collectibles. Axie Infinity and Decentraland cover play-and-create worlds. Each one still signs with Trust Vault."
      image="/images/pages/page-nfts.png"
      alt="A gallery of neon digital collectibles"
      actions={<PillLink to="/dapps?filter=NFT">Open the NFT filter</PillLink>}
      cards={[
        { title: 'OpenSea', body: 'Browse crypto collectibles and non-fungible tokens from the NFT filter, then connect the vault wallet to continue.' },
        { title: 'Axie Infinity', body: 'A blockchain game of trading and battling. It sits under Gaming in the same directory.' },
        { title: 'Decentraland', body: 'A virtual world on Ethereum where creators publish and monetize spaces, opened from the same list.' }
      ]}
    />
  );
}

export function EscrowPage({ shell = true }) {
  return (
    <IllustratedPage
      shell={shell}
      eyebrow="Escrow"
      title="Hold funds until"
      accent="both sides are ready."
      lede="Escrow keeps assets in the Trust Vault between two parties. Nothing is handed to a separate custodian account. Release happens when the condition you set is met."
      image="/images/pages/page-escrow.png"
      alt="A lock holding funds between two wallets"
      actions={<PillLink to="/auth">Connect Wallet</PillLink>}
      cards={[
        { title: 'Fund the hold', body: 'Move the amount from your wallet into the escrow hold. The rest of the portfolio stays available.' },
        { title: 'Set the condition', body: 'Both sides can see what must happen before the funds move. The terms stay attached to the hold.' },
        { title: 'Release or return', body: 'When the condition is met, funds release. If it is not, they can return to the funding wallet.' }
      ]}
    />
  );
}

export function SupportPage({ shell = true }) {
  return (
    <IllustratedPage
      shell={shell}
      eyebrow="Support"
      title="Help for the wallet,"
      accent="plans, and DApps."
      lede="Start with the path that matches the question. Connection issues use the wallet guide. Plan questions use the weekly tiers. App questions start in the directory, where each product explains itself before you open it."
      image="/images/pages/page-support.png"
      alt="Support panel beside a Trust Vault wallet"
      actions={<><PillLink to="/documentation">View documentation</PillLink><PillLink to="/auth" variant="ghost">Sign in</PillLink></>}
      cards={[
        { title: 'Wallet will not connect', body: 'Use the documentation to connect or create a wallet, then return to Sign In and approve the signature.' },
        { title: 'Which plan is which', body: 'Invest lists 8%, 10%, and 12% weekly tiers. The connected plan screen is the one that takes the funding.' },
        { title: 'Which DApp to open', body: 'Filter the directory by DeFi, NFT, Gaming, or Exchange, read the card, then open it with the vault wallet.' }
      ]}
    />
  );
}
